import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Building2, Plus, Loader2, Users, Baby, DollarSign, ShieldAlert,
  CheckCircle2, PauseCircle, PlayCircle, Trash2, LogOut, Send, Pencil, Mail,
  FileText, Sparkles, AlertTriangle, Download, Bug, ChevronDown, ChevronRight, FileSpreadsheet, Upload, Printer, Stethoscope, Truck, ClipboardList, Link as LinkIcon, Check, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { RecordsAccessToggles } from "@/components/superadmin/RecordsAccessToggles";

const STATUS_META = {
  active:    { label: "Active",    class: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  trialing:  { label: "Trial",     class: "bg-sky-50 text-sky-800 border-sky-300" },
  past_due:  { label: "Past due",  class: "bg-amber-50 text-amber-800 border-amber-300" },
  suspended: { label: "Suspended", class: "bg-red-50 text-red-800 border-red-300" },
  canceled:  { label: "Canceled",  class: "bg-stone-100 text-stone-700 border-stone-300" },
  inactive:  { label: "Inactive",  class: "bg-stone-100 text-stone-700 border-stone-300" },
};

const emptyDraft = () => ({
  center_name: "",
  owner_name: "",
  owner_email: "",
  owner_password: "",
  license_number: "",
  address: "",
  plan_id: "growth",
});

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {center, draft}
  const [mailing, setMailing] = useState(null); // {center, subject, body, to}
  const [invoicing, setInvoicing] = useState(null); // {center, items, period_label, paid, notes, useAi}
  const [failedPayments, setFailedPayments] = useState({ count: 0, rows: [] });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/superadmin/stats"),
      api.get("/superadmin/centers"),
      api.get("/superadmin/payments/failed"),
    ])
      .then(([a, b, c]) => { setStats(a.data); setCenters(b.data); setFailedPayments(c.data); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const packages = stats?.packages || [];
  const filtered = useMemo(() => centers, [centers]);

  const doLogout = () => { logout(); toast.success("Signed out"); nav("/", { replace: true }); };

  const sendTestEmail = async () => {
    try {
      const res = await api.post("/superadmin/test-email");
      if (res.data?.sent) toast.success(`Test email sent via ${res.data.provider} to ${res.data.to}`);
      else toast.error(`Email not sent — ${res.data?.reason || "no provider configured"}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.center_name || !draft.owner_name || !draft.owner_email) {
      return toast.error("Center, owner name, and email are required.");
    }
    if (draft.owner_password && draft.owner_password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      const payload = { ...draft };
      if (!payload.owner_password) delete payload.owner_password;
      const res = await api.post("/superadmin/centers", payload);
      if (res.data?.invite) {
        toast.success(res.data.invite.sent
          ? `${draft.center_name} onboarded — invite link emailed to ${res.data.invite.to}.`
          : `${draft.center_name} onboarded. Invite could not be emailed (${res.data.invite.reason}).`);
      } else {
        toast.success(`${draft.center_name} onboarded.`);
      }
      setOpen(false); setDraft(emptyDraft()); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const updateSubscription = async (centerId, patch) => {
    try {
      await api.put(`/superadmin/centers/${centerId}/subscription`, patch);
      toast.success("Subscription updated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteCenter = async (c) => {
    if (!window.confirm(`Permanently delete "${c.name}"? All children, staff, attendance, and owner account will be removed.`)) return;
    try { await api.delete(`/superadmin/centers/${c.id}`); toast.success("Center removed."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (c) => setEditing({
    center: c,
    draft: {
      center_name: c.name || "", license_number: c.license_number || "",
      program_number: c.program_number || "", address: c.address || "",
      owner_name: c.owner?.name || "", owner_email: c.owner?.email || "", owner_password: "",
    },
  });

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      const d = editing.draft;
      const payload = {
        center_name: d.center_name, license_number: d.license_number,
        program_number: d.program_number, address: d.address,
        owner_name: d.owner_name, owner_email: d.owner_email,
        ...(d.owner_password ? { owner_password: d.owner_password } : {}),
      };
      await api.put(`/superadmin/centers/${editing.center.id}`, payload);
      toast.success("Center updated.");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const openMail = (c) => setMailing({ center: c, subject: "", body: "", to: c.owner?.email || "" });

  const resendInvite = async (c) => {
    try {
      const res = await api.post(`/superadmin/centers/${c.id}/invite`);
      if (res.data?.sent) toast.success(`Invite link emailed to ${res.data.to}`);
      else toast.error(`Invite not sent — ${res.data?.reason || "unknown"}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendMail = async (e) => {
    e.preventDefault();
    if (!mailing) return;
    if (!mailing.subject.trim() || !mailing.body.trim()) return toast.error("Subject and body are required.");
    setBusy(true);
    try {
      const res = await api.post(`/superadmin/centers/${mailing.center.id}/email`, {
        subject: mailing.subject, body: mailing.body,
        ...(mailing.to && mailing.to !== mailing.center.owner?.email ? { to_email: mailing.to } : {}),
      });
      if (res.data?.sent) toast.success(`Sent to ${res.data.to}`);
      else toast.error(`Not sent — ${res.data?.reason || "unknown"}`);
      setMailing(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  // ---- Invoices --------------------------------------------------
  const openInvoice = (c) => {
    const planAmount = c.subscription?.plan_id
      ? (packages.find((p) => p.id === c.subscription.plan_id)?.amount || 0)
      : 0;
    const planName = packages.find((p) => p.id === c.subscription?.plan_id)?.name || "ChildCheck";
    const today = new Date();
    setInvoicing({
      center: c,
      items: planAmount > 0
        ? [{ description: `ChildCheck ${planName} — monthly subscription`, quantity: 1, unit_price: planAmount }]
        : [{ description: "", quantity: 1, unit_price: 0 }],
      period_label: today.toLocaleString("en-US", { month: "long", year: "numeric" }),
      paid: false,
      notes: "",
      useAi: true,
    });
  };

  const updateItem = (idx, patch) => {
    setInvoicing((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };
  const addItem = () =>
    setInvoicing((prev) => ({ ...prev, items: [...prev.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) =>
    setInvoicing((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const sendInvoice = async (e) => {
    e.preventDefault();
    if (!invoicing) return;
    const items = invoicing.items.filter((it) => it.description.trim() && Number(it.unit_price) > 0);
    if (items.length === 0) return toast.error("Add at least one line item with a description and unit price.");
    setBusy(true);
    try {
      const res = await api.post(`/superadmin/centers/${invoicing.center.id}/send-invoice`, {
        line_items: items.map((it) => ({
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
        })),
        period_label: invoicing.period_label || null,
        notes: invoicing.notes || null,
        use_ai_thank_you: !!invoicing.useAi,
        paid: !!invoicing.paid,
      });
      // iter-76: backend now returns 202 immediately; PDF+email finish in the
      // background so the modal closes in <300ms instead of blocking 5-10s.
      toast.success(
        `Invoice ${res.data?.invoice_number} queued for ${res.data?.to} — it will arrive within a minute.`
      );
      setInvoicing(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const invoiceTotal = useMemo(() => {
    if (!invoicing) return 0;
    return invoicing.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  }, [invoicing]);

  return (
    <div data-testid="superadmin-page" className="min-h-screen bg-stone-50">
      {/* Top bar */}
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="ChildCheckOh" className="w-8 h-8" />
            <span className="font-display text-lg font-semibold tracking-tight text-stone-900">ChildCheck <span className="text-emerald-700">Ohio</span></span>
          </Link>
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-md bg-red-50 text-red-800 border border-red-200" data-testid="sa-role-badge">Superadmin</span>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-sm font-medium text-stone-900" data-testid="sa-user-name">{user?.name}</div>
              <div className="text-[11px] text-stone-500" data-testid="sa-user-email">{user?.email}</div>
            </div>
            <Button data-testid="sa-logout-btn" variant="ghost" size="sm" onClick={doLogout}><LogOut className="w-4 h-4 mr-1" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Operator console</div>
            <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Centers & subscriptions</h1>
            <p className="mt-2 text-stone-500 max-w-2xl">Onboard new childcare centers, set their subscription tier, and monitor live MRR across every ChildCheck tenant.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="sa-test-email-btn" variant="outline" onClick={sendTestEmail} type="button" className="rounded-xl h-11">
              <Send className="w-4 h-4 mr-1" /> Send test email
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="sa-new-center-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
                  <Plus className="w-4 h-4 mr-1" /> Onboard new center
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Onboard a new center</DialogTitle>
                <DialogDescription>Creates the center record, an owner account, and (optionally) attaches a subscription plan. The owner can sign in at /login with the email + password below.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 pt-2" data-testid="sa-new-center-form">
                <Field label="Center name" value={draft.center_name} onChange={(v) => setDraft({ ...draft, center_name: v })} testId="sa-f-center" required />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="License #" value={draft.license_number} onChange={(v) => setDraft({ ...draft, license_number: v })} testId="sa-f-license" />
                  <Field label="Address" value={draft.address} onChange={(v) => setDraft({ ...draft, address: v })} testId="sa-f-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Owner name" value={draft.owner_name} onChange={(v) => setDraft({ ...draft, owner_name: v })} testId="sa-f-owner-name" required />
                  <Field label="Owner email" type="email" value={draft.owner_email} onChange={(v) => setDraft({ ...draft, owner_email: v })} testId="sa-f-owner-email" required />
                </div>
                <Field label="Owner password (leave blank to send a secure invite link)" type="password" value={draft.owner_password} onChange={(v) => setDraft({ ...draft, owner_password: v })} testId="sa-f-owner-password" />
                <p className="-mt-2 text-[11px] text-stone-500">
                  Recommended: leave blank. The owner will receive a one-time email with a secure link to set their own password — no plaintext password travels over email.
                </p>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Subscription plan</Label>
                  <Select value={draft.plan_id || "none"} onValueChange={(v) => setDraft({ ...draft, plan_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="sa-f-plan" className="mt-2 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No plan yet (inactive)</SelectItem>
                      {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — ${p.amount}/mo</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="sa-f-submit" type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Onboard center"}
                </Button>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard testId="sa-stat-centers"  label="Centers"    value={stats?.centers_total ?? "—"}  Icon={Building2} tone="emerald" />
          <StatCard testId="sa-stat-active"   label="Active"     value={stats?.centers_active ?? "—"} Icon={CheckCircle2} tone="emerald" />
          <StatCard testId="sa-stat-suspended" label="Suspended" value={stats?.centers_suspended ?? "—"} Icon={ShieldAlert} tone="red" />
          <StatCard testId="sa-stat-mrr"      label="MRR"        value={stats ? `$${(stats.mrr_usd||0).toLocaleString()}` : "—"} Icon={DollarSign} tone="stone" />
          <StatCard testId="sa-stat-children" label="Children"   value={stats?.children_total ?? "—"} Icon={Baby} tone="orange" />
        </div>

        {/* Failed payments — auto-shown only when there's something to triage */}
        {failedPayments.count > 0 && (
          <div data-testid="sa-failed-payments" className="rounded-2xl border border-red-200 bg-red-50/50 p-5">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-700" />
              <div className="flex-1">
                <div className="font-display text-lg text-red-900">{failedPayments.count} payment{failedPayments.count === 1 ? "" : "s"} failed</div>
                <div className="text-xs text-red-800/80">PayPal couldn't collect these subscription charges. Email the owners to update their payment method.</div>
              </div>
            </div>
            <div className="space-y-2">
              {failedPayments.rows.slice(0, 5).map((row) => (
                <div key={row.subscription_id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-red-200">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{row.center_name || row.subscription_id}</div>
                    <div className="text-[11px] text-stone-500 truncate">{row.owner_email || "no owner email"} · ${Number(row.amount || 0).toFixed(2)} · {row.payment_status}</div>
                  </div>
                  {row.center_id && (
                    <Button data-testid={`sa-failed-email-${row.subscription_id}`} variant="ghost" size="sm" onClick={() => {
                      const c = centers.find((x) => x.id === row.center_id);
                      if (c) openMail(c);
                    }} className="text-emerald-700">
                      <Mail className="w-3.5 h-3.5 mr-1" /> Email owner
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <FrontendErrorsPanel />

        <TenantAuditPanel />

        <MedicalBillingPanel />

        <ReminderSettingsPanel />

        <InsuranceQuotesPanel />

        {/* Centers table */}
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="p-5 border-b border-stone-200 flex items-center justify-between">
            <div>
              <div className="font-display text-xl text-stone-900">All centers</div>
              <div className="text-xs text-stone-500">{filtered.length} total</div>
            </div>
          </div>
          {loading ? (
            <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-14 text-center">
              <Building2 className="w-8 h-8 mx-auto text-stone-300" />
              <p className="mt-3 font-display text-xl text-stone-900">No centers yet.</p>
              <p className="mt-1 text-sm text-stone-500">Click <b>Onboard new center</b> to create your first tenant.</p>
            </div>
          ) : (
            <table className="w-full" data-testid="sa-centers-table">
              <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="text-left font-semibold px-5 py-3">Center</th>
                  <th className="text-left font-semibold px-5 py-3">Owner</th>
                  <th className="text-left font-semibold px-5 py-3">Plan</th>
                  <th className="text-left font-semibold px-5 py-3">Status</th>
                  <th className="text-right font-semibold px-5 py-3"><Users className="w-3.5 h-3.5 inline mr-1" />Staff</th>
                  <th className="text-right font-semibold px-5 py-3"><Baby className="w-3.5 h-3.5 inline mr-1" />Kids</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const sub = c.subscription || {};
                  const sm = STATUS_META[sub.status || "inactive"] || STATUS_META.inactive;
                  return (
                    <tr key={c.id} data-testid={`sa-center-row-${c.id}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                      <td className="px-5 py-4">
                        <div className="font-medium text-stone-900">{c.name}</div>
                        <div className="text-[11px] text-stone-500">{c.license_number || "—"} · {c.address || "no address"}</div>
                        <RecordsAccessToggles key={`${c.id}-${JSON.stringify(c.records_features || {})}`} center={c} />
                      </td>
                      <td className="px-5 py-4 text-sm text-stone-700">
                        <div>{c.owner?.name || "—"}</div>
                        <div className="text-[11px] text-stone-500 flex items-center gap-1.5">
                          {c.owner?.email || "—"}
                          {c.owner?.pending_invite && (
                            <span data-testid={`sa-pending-invite-${c.id}`} className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                              Invite pending
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <Select value={sub.plan_id || "none"} onValueChange={(v) => updateSubscription(c.id, { plan_id: v === "none" ? null : v })}>
                          <SelectTrigger data-testid={`sa-plan-${c.id}`} className="h-9 w-40 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No plan</SelectItem>
                            {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · ${p.amount}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-md border ${sm.class}`} data-testid={`sa-status-${c.id}`}>{sm.label}</span>
                      </td>
                      <td className="px-5 py-4 text-right text-sm text-stone-700">{c.staff_count}</td>
                      <td className="px-5 py-4 text-right text-sm text-stone-700">{c.children_count}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button data-testid={`sa-email-${c.id}`} variant="ghost" size="sm" onClick={() => openMail(c)} className="text-emerald-700">
                            <Mail className="w-4 h-4 mr-1" /> Email
                          </Button>
                          <Button data-testid={`sa-invoice-${c.id}`} variant="ghost" size="sm" onClick={() => openInvoice(c)} className="text-orange-700">
                            <FileText className="w-4 h-4 mr-1" /> Invoice
                          </Button>
                          <Button data-testid={`sa-edit-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)} className="text-stone-700">
                            <Pencil className="w-4 h-4 mr-1" /> Edit
                          </Button>
                          {sub.status === "suspended" ? (
                            <Button data-testid={`sa-reactivate-${c.id}`} variant="ghost" size="sm" onClick={() => updateSubscription(c.id, { status: "active" })} className="text-emerald-700">
                              <PlayCircle className="w-4 h-4 mr-1" /> Reactivate
                            </Button>
                          ) : (
                            <Button data-testid={`sa-suspend-${c.id}`} variant="ghost" size="sm" onClick={() => updateSubscription(c.id, { status: "suspended" })} className="text-amber-700">
                              <PauseCircle className="w-4 h-4 mr-1" /> Suspend
                            </Button>
                          )}
                          <Button data-testid={`sa-delete-${c.id}`} variant="ghost" size="sm" onClick={() => deleteCenter(c)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Edit center dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="sa-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editing?.center?.name}</DialogTitle>
            <DialogDescription>Update the center record and (optionally) the owner's profile or password.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4 pt-2">
              <Field label="Center name" value={editing.draft.center_name} onChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, center_name: v } })} testId="sa-edit-center" required />
              <div className="grid grid-cols-3 gap-3">
                <Field label="License #" value={editing.draft.license_number} onChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, license_number: v } })} testId="sa-edit-license" />
                <Field label="Program #" value={editing.draft.program_number} onChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, program_number: v } })} testId="sa-edit-program" />
                <Field label="Address" value={editing.draft.address} onChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, address: v } })} testId="sa-edit-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Owner name" value={editing.draft.owner_name} onChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, owner_name: v } })} testId="sa-edit-owner-name" />
                <Field label="Owner email" type="email" value={editing.draft.owner_email} onChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, owner_email: v } })} testId="sa-edit-owner-email" />
              </div>
              <Field label="Reset password (leave blank to keep)" type="password" value={editing.draft.owner_password} onChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, owner_password: v } })} testId="sa-edit-owner-password" />
              <div className="rounded-xl border border-dashed border-emerald-300 bg-emerald-50 p-3 flex items-center justify-between gap-3">
                <div className="text-xs text-emerald-900 leading-snug">
                  <b>Safer alternative:</b> email the owner a one-time link to set their own password — no plaintext ever travels over email.
                </div>
                <Button type="button" data-testid="sa-edit-resend-invite" variant="outline" onClick={() => resendInvite(editing.center)} className="rounded-lg h-9 shrink-0">
                  <Mail className="w-3.5 h-3.5 mr-1" /> Send invite
                </Button>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="flex-1 rounded-xl h-11">Cancel</Button>
                <Button type="submit" data-testid="sa-edit-submit" disabled={busy} className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save changes"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Email center dialog */}
      <Dialog open={!!mailing} onOpenChange={(o) => !o && setMailing(null)}>
        <DialogContent className="max-w-xl" data-testid="sa-email-dialog">
          <DialogHeader>
            <DialogTitle>Email {mailing?.center?.name}</DialogTitle>
            <DialogDescription>
              Sends a branded HTML email via Gmail SMTP (or SendGrid) to the center's owner. Owner: <b>{mailing?.center?.owner?.email || "—"}</b>
            </DialogDescription>
          </DialogHeader>
          {mailing && (
            <form onSubmit={sendMail} className="space-y-4 pt-2">
              <Field label="Recipient (owner email or override)" type="email" value={mailing.to} onChange={(v) => setMailing({ ...mailing, to: v })} testId="sa-email-to" required />
              <Field label="Subject" value={mailing.subject} onChange={(v) => setMailing({ ...mailing, subject: v })} testId="sa-email-subject" required />
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Message</Label>
                <Textarea
                  data-testid="sa-email-body"
                  value={mailing.body}
                  onChange={(e) => setMailing({ ...mailing, body: e.target.value })}
                  rows={7}
                  placeholder={"Hi " + (mailing.center?.owner?.name || "") + ",\n\nQuick note about your ChildCheck account…\n\nThanks,"}
                  className="mt-2"
                  required
                />
                <p className="text-[11px] text-stone-400 mt-1">Blank lines become separate paragraphs in the email.</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setMailing(null)} className="flex-1 rounded-xl h-11">Cancel</Button>
                <Button type="submit" data-testid="sa-email-submit" disabled={busy} className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1" /> Send email</>}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Send invoice dialog */}
      <Dialog open={!!invoicing} onOpenChange={(o) => !o && setInvoicing(null)}>
        <DialogContent className="max-w-2xl" data-testid="sa-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Send invoice to {invoicing?.center?.name}</DialogTitle>
            <DialogDescription>
              Renders a branded PDF invoice and emails it to <b>{invoicing?.center?.owner?.email || "—"}</b>. Optionally adds an AI-written thank-you note via Gemini.
            </DialogDescription>
          </DialogHeader>
          {invoicing && (
            <form onSubmit={sendInvoice} className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Period label" value={invoicing.period_label}
                       onChange={(v) => setInvoicing({ ...invoicing, period_label: v })}
                       testId="sa-inv-period" />
                <div className="flex items-end gap-3 pb-1">
                  <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                    <input data-testid="sa-inv-paid" type="checkbox" checked={invoicing.paid}
                           onChange={(e) => setInvoicing({ ...invoicing, paid: e.target.checked })}
                           className="w-4 h-4 accent-emerald-700" />
                    Mark as paid (receipt)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                    <input data-testid="sa-inv-ai" type="checkbox" checked={invoicing.useAi}
                           onChange={(e) => setInvoicing({ ...invoicing, useAi: e.target.checked })}
                           className="w-4 h-4 accent-emerald-700" />
                    <Sparkles className="w-3.5 h-3.5 text-emerald-700" /> AI thank-you note
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Line items</Label>
                <div className="mt-2 space-y-2">
                  {invoicing.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`sa-inv-row-${i}`}>
                      <Input className="col-span-7" placeholder="Description"
                             value={it.description}
                             onChange={(e) => updateItem(i, { description: e.target.value })}
                             data-testid={`sa-inv-desc-${i}`} />
                      <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="Qty"
                             value={it.quantity}
                             onChange={(e) => updateItem(i, { quantity: e.target.value })}
                             data-testid={`sa-inv-qty-${i}`} />
                      <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="$"
                             value={it.unit_price}
                             onChange={(e) => updateItem(i, { unit_price: e.target.value })}
                             data-testid={`sa-inv-price-${i}`} />
                      <Button type="button" variant="ghost" size="icon"
                              onClick={() => removeItem(i)}
                              disabled={invoicing.items.length === 1}
                              className="col-span-1 text-red-600 h-9 w-9"
                              data-testid={`sa-inv-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addItem}
                        className="mt-2 rounded-lg" data-testid="sa-inv-add-item">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add line item
                </Button>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Notes (printed on the PDF)</Label>
                <Textarea
                  data-testid="sa-inv-notes"
                  value={invoicing.notes}
                  onChange={(e) => setInvoicing({ ...invoicing, notes: e.target.value })}
                  rows={2}
                  placeholder="e.g. Custom onboarding bundle. Net 7."
                  className="mt-2"
                />
              </div>

              <div className="flex items-center justify-between rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
                <div className="text-xs uppercase tracking-widest text-stone-500">Total</div>
                <div className="font-display text-2xl text-stone-900" data-testid="sa-inv-total">
                  ${invoiceTotal.toFixed(2)}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setInvoicing(null)} className="flex-1 rounded-xl h-11">Cancel</Button>
                <Button type="submit" data-testid="sa-inv-submit" disabled={busy || invoiceTotal <= 0}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl h-11">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Queueing…</> : <><FileText className="w-4 h-4 mr-1" /> Send invoice</>}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, Icon, tone = "stone", testId }) {
  const tones = { emerald: "text-emerald-700 bg-emerald-50", red: "text-red-700 bg-red-50", orange: "text-orange-700 bg-orange-50", stone: "text-stone-700 bg-stone-100" };
  return (
    <div data-testid={testId} className="rounded-2xl border border-stone-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">{label}</div>
        <span className={`w-7 h-7 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="w-3.5 h-3.5" /></span>
      </div>
      <div className="mt-2 font-display text-3xl font-medium text-stone-900">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", required = false }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-stone-500">{label}{required && <span className="text-red-600"> *</span>}</Label>
      <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} type={type} required={required} className="mt-2 h-11" />
    </div>
  );
}



// --- iter-81l: Tenant Audit Panel — drill_logs cross-tenant leakage tool -----
function TenantAuditPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get("/superadmin/drill-logs/audit");
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const purge = async () => {
    if (!window.confirm(
      `This will DELETE ${data?.orphans?.length || 0} orphaned drill-log center groups + null-center-id rows. Continue?`
    )) return;
    setPurging(true);
    try {
      const r = await api.post("/superadmin/drill-logs/purge-orphans?dry_run=false");
      toast.success(`Purged ${r.data.deleted} orphaned drill-log rows.`);
      await run();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPurging(false); }
  };

  const orphanCount = (data?.orphans || []).length;
  const nullCount = (data?.null_center_id || 0) + (data?.missing_center_id || 0) + (data?.empty_string_center_id || 0);
  const dirty = orphanCount + nullCount > 0;

  return (
    <div data-testid="tenant-audit-panel" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
      <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700">Data Integrity</div>
          <div className="font-display text-xl font-medium text-stone-900">Drill Log tenant audit</div>
          <div className="text-xs text-stone-500 mt-1 max-w-2xl">
            Scans <code>drill_logs</code> for rows whose <code>center_id</code> no longer points to a live center, or is null / empty. Use when a center owner reports seeing another daycare&apos;s data.
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="tenant-audit-run" onClick={run} disabled={loading} className="rounded-xl h-10">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Run audit
          </Button>
          {dirty && (
            <Button
              data-testid="tenant-audit-purge"
              onClick={purge}
              disabled={purging}
              className="rounded-xl h-10 bg-red-600 hover:bg-red-700 text-white"
            >
              {purging ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Purge orphans ({orphanCount + nullCount})
            </Button>
          )}
        </div>
      </div>
      {data && (
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-4 gap-3">
            <div data-testid="audit-total" className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Total drill logs</div>
              <div className="text-xl font-semibold text-stone-900">{data.total_drill_logs}</div>
            </div>
            <div data-testid="audit-null" className={`rounded-xl border px-3 py-2 ${nullCount > 0 ? "border-red-300 bg-red-50" : "border-stone-200 bg-stone-50"}`}>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Null / missing center_id</div>
              <div className={`text-xl font-semibold ${nullCount > 0 ? "text-red-700" : "text-stone-900"}`}>{nullCount}</div>
            </div>
            <div data-testid="audit-orphans" className={`rounded-xl border px-3 py-2 ${orphanCount > 0 ? "border-red-300 bg-red-50" : "border-stone-200 bg-stone-50"}`}>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Orphan center groups</div>
              <div className={`text-xl font-semibold ${orphanCount > 0 ? "text-red-700" : "text-stone-900"}`}>{orphanCount}</div>
            </div>
            <div data-testid="audit-centers" className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Centers with drill logs</div>
              <div className="text-xl font-semibold text-stone-900">{(data.by_center || []).filter(c => !c.orphan).length}</div>
            </div>
          </div>
          <div className="rounded-xl border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-[10px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="text-left px-3 py-2">Center</th>
                  <th className="text-left px-3 py-2">Center ID</th>
                  <th className="text-right px-3 py-2">Rows</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {(data.by_center || []).map((row) => (
                  <tr key={row.center_id} data-testid={`audit-row-${row.center_id}`} className={row.orphan ? "bg-red-50" : ""}>
                    <td className="px-3 py-2 text-stone-900">{row.name || <em className="text-stone-400">Unknown</em>}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-stone-500">{row.center_id.slice(0, 12)}…</td>
                    <td className="px-3 py-2 text-right">{row.rows}</td>
                    <td className="px-3 py-2">
                      {row.orphan
                        ? <span className="inline-flex px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">Orphan</span>
                        : <span className="inline-flex px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">OK</span>}
                    </td>
                  </tr>
                ))}
                {(data.by_center || []).length === 0 && (
                  <tr><td colSpan={4} className="text-center py-4 text-stone-500 text-xs">No drill logs found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Frontend Errors Panel (iter-61 safety net dashboard) ---------------
function FrontendErrorsPanel() {
  const [data, setData] = useState({ errors: [], stats: { total: 0, last_24h: 0, unique: 0 } });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // error key

  const load = () => {
    setLoading(true);
    api.get("/superadmin/client-errors")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const clearAll = async () => {
    if (!window.confirm(`Clear all ${data.stats.total} captured frontend errors?`)) return;
    try {
      await api.delete("/superadmin/client-errors");
      toast.success("Frontend error log cleared.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <Loader2 className="w-4 h-4 animate-spin text-stone-500 inline mr-2" />
        <span className="text-xs text-stone-500">Loading client errors…</span>
      </div>
    );
  }

  if (data.errors.length === 0) {
    return (
      <div data-testid="sa-client-errors-empty" className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-700" />
          <div>
            <div className="font-display text-base text-emerald-900">No frontend errors captured.</div>
            <div className="text-xs text-stone-600">The 4-layer safety net is up — any uncaught crash on any center&apos;s app will show up here.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sa-client-errors" className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Bug className="w-5 h-5 text-amber-700" />
          <div>
            <div className="font-display text-xl text-stone-900">Frontend errors</div>
            <div className="text-xs text-stone-600">
              <strong>{data.stats.unique}</strong> unique error{data.stats.unique === 1 ? "" : "s"} ·
              <strong className="ml-1">{data.stats.last_24h}</strong> in last 24h ·
              <strong className="ml-1">{data.stats.total}</strong> total captured
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="sa-client-errors-refresh" variant="outline" size="sm" onClick={load} className="rounded-lg h-9 text-xs">Refresh</Button>
          <Button data-testid="sa-client-errors-clear" variant="outline" size="sm" onClick={clearAll} className="rounded-lg h-9 text-xs border-red-300 text-red-700 hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear all
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="divide-y divide-stone-100">
          {data.errors.map((err, idx) => {
            const key = `${err.kind}-${idx}`;
            const isOpen = expanded === key;
            const lastSeen = err.last_seen ? new Date(err.last_seen) : null;
            const ago = lastSeen ? formatAgo(lastSeen) : "—";
            const kindColor = err.kind === "react_render" ? "bg-red-100 text-red-700 border-red-300" :
              err.kind === "unhandled_promise" ? "bg-amber-100 text-amber-700 border-amber-300" :
                "bg-stone-100 text-stone-700 border-stone-300";
            return (
              <div key={key} data-testid={`sa-client-error-row-${idx}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : key)}
                  className="w-full px-4 py-3 text-left hover:bg-stone-50 transition-colors flex items-start gap-3"
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${kindColor}`}>{err.kind || "unknown"}</span>
                      {err.count > 1 && (
                        <span className="inline-flex items-center rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">×{err.count}</span>
                      )}
                      <span className="text-[11px] text-stone-500">{ago}</span>
                    </div>
                    <div className="font-mono text-sm text-stone-900 truncate">{err.message || "(no message)"}</div>
                    {err.url && <div className="text-[11px] text-stone-500 truncate mt-0.5">{stripDomain(err.url)}</div>}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pl-11 space-y-2 bg-stone-50/50 border-t border-stone-100">
                    <DetailRow label="URL" value={err.url} />
                    <DetailRow label="User agent" value={err.user_agent} />
                    <DetailRow label="Source" value={err.source ? `${err.source}:${err.line ?? "?"}:${err.column ?? "?"}` : null} />
                    <DetailRow label="Last seen" value={err.last_seen} />
                    {err.stack && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1 mt-2">Stack trace</div>
                        <pre className="text-[11px] bg-stone-900 text-stone-200 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">{err.stack}</pre>
                      </div>
                    )}
                    {err.component_stack && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1 mt-2">React component stack</div>
                        <pre className="text-[11px] bg-stone-900 text-stone-200 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">{err.component_stack}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="text-stone-500 uppercase tracking-widest text-[10px] font-semibold w-28 flex-shrink-0">{label}</div>
      <div className="font-mono text-stone-700 break-all flex-1 min-w-0">{value}</div>
    </div>
  );
}

function stripDomain(url) {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch { return url; }
}

function money(n) {
  return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatAgo(d) {
  const ms = Date.now() - d.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

// --- Medical Billing Panel (iter-61) ------------------------------------
// SuperAdmin uploads HHA-style claim CSVs (e.g. "Claim Search Results.csv").
// Provider/EDI columns are stripped server-side; this panel surfaces the
// client side: who was billed, how much, paid vs outstanding, with a one-click
// printable roster for desk lookup during family conversations.
function MedicalBillingPanel() {
  const [data, setData] = useState({ claims: [], stats: { count: 0, unique_clients: 0, total_billed: 0, total_paid: 0, total_outstanding: 0, total_patient_responsibility: 0 } });
  const [byClient, setByClient] = useState({ clients: [], totals: { client_count: 0, line_count: 0, total_billed: 0, total_paid: 0, total_outstanding: 0 } });
  const [view, setView] = useState("by_client"); // "by_client" (default — director's request) | "by_line"
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [heading, setHeading] = useState("");
  const [headingSaved, setHeadingSaved] = useState("");
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/superadmin/medical-billing/claims", { params: query ? { q: query } : {} }).then((r) => r.data),
      api.get("/superadmin/medical-billing/by-client").then((r) => r.data),
      api.get("/superadmin/medical-billing/settings").then((r) => r.data),
    ])
      .then(([lineData, clientData, settings]) => {
        setData(lineData);
        setByClient(clientData);
        setHeading(settings?.roster_heading || "");
        setHeadingSaved(settings?.roster_heading || "");
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [query]);

  const saveHeading = async () => {
    try {
      const r = await api.put("/superadmin/medical-billing/settings", { roster_heading: heading });
      setHeadingSaved(r.data?.roster_heading || "");
      toast.success("Roster heading saved.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      toast.error("Please upload a .csv or .xlsx file.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/superadmin/medical-billing/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { inserted, updated, skipped } = r.data || {};
      toast.success(`Imported ${inserted} new · ${updated} updated${skipped ? ` · ${skipped} skipped` : ""}.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const printPdf = async () => {
    try {
      const res = await api.get("/superadmin/medical-billing/claims.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Remove this claim line?")) return;
    try { await api.delete(`/superadmin/medical-billing/claims/${id}`); toast.success("Removed."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete all ${data.stats.count} claim lines?`)) return;
    try { await api.delete("/superadmin/medical-billing/claims"); toast.success("All claims cleared."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="sa-medical-billing" className="rounded-2xl border border-stone-200 bg-white p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-700 text-white grid place-items-center">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-xl text-stone-900">Medical billing</div>
            <div className="text-xs text-stone-600">Upload HHA / clearing-house claim CSVs. Provider info stripped; client view only.</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={onUpload} data-testid="sa-billing-file-input" />
          <Button data-testid="sa-billing-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-9 text-xs">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            {busy ? "Importing…" : "Upload CSV / XLSX"}
          </Button>
          {data.stats.count > 0 && (
            <>
              <Button data-testid="sa-billing-print-btn" variant="outline" onClick={printPdf} className="rounded-lg h-9 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <Printer className="w-3.5 h-3.5 mr-1" /> Print roster
              </Button>
              <Button data-testid="sa-billing-clear-btn" variant="outline" onClick={clearAll} className="rounded-lg h-9 text-xs border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear all
              </Button>
            </>
          )}
        </div>
      </div>

      {data.stats.count > 0 && (
        <div className="grid sm:grid-cols-5 gap-3 mb-4">
          <StatTile label="Claim lines" value={data.stats.count} />
          <StatTile label="Unique clients" value={data.stats.unique_clients} />
          <StatTile label="Total billed" value={money(data.stats.total_billed)} tone="stone" />
          <StatTile label="Total paid" value={money(data.stats.total_paid)} tone="emerald" />
          <StatTile label="Outstanding" value={money(data.stats.total_outstanding)} tone={data.stats.total_outstanding > 0 ? "red" : "emerald"} />
        </div>
      )}

      {data.stats.count > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap text-xs">
          <span className="text-stone-600 font-medium">Roster heading:</span>
          <input
            data-testid="sa-billing-heading-input"
            type="text"
            value={heading}
            placeholder="e.g. Your Center Name — Client Roster"
            onChange={(e) => setHeading(e.target.value)}
            className="flex-1 min-w-[260px] max-w-[440px] h-8 px-3 rounded-lg border border-stone-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-200 outline-none"
            maxLength={120}
          />
          <Button
            data-testid="sa-billing-heading-save"
            onClick={saveHeading}
            disabled={heading === headingSaved}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-8 text-xs px-3 disabled:opacity-50"
          >Save</Button>
          <span className="text-stone-500">
            {headingSaved ? `Prints as “${headingSaved}”.` : "Defaults to “Medical Billing — Client Roster”."}
          </span>
        </div>
      )}

      {data.stats.count > 0 && (
        <div className="mb-3 flex items-center gap-3 flex-wrap">
          <div data-testid="sa-billing-view-toggle" className="inline-flex border border-stone-300 rounded-xl bg-white overflow-hidden">
            <button
              data-testid="sa-billing-view-by-client"
              onClick={() => setView("by_client")}
              className={`px-3 py-2 text-xs font-medium transition-colors ${view === "by_client" ? "bg-emerald-700 text-white" : "text-stone-700 hover:bg-stone-50"}`}
            >By client</button>
            <button
              data-testid="sa-billing-view-by-line"
              onClick={() => setView("by_line")}
              className={`px-3 py-2 text-xs font-medium transition-colors border-l border-stone-300 ${view === "by_line" ? "bg-emerald-700 text-white" : "text-stone-700 hover:bg-stone-50"}`}
            >By claim line</button>
          </div>
          <Input
            data-testid="sa-billing-search"
            placeholder={view === "by_client" ? "Filter clients by name…" : "Search by client name, payer, status…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-10 flex-1 min-w-[240px]"
          />
        </div>
      )}

      <div className="rounded-xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
        ) : view === "by_client" ? (
          // ===== BY-CLIENT VIEW (rollup per family) =====
          (() => {
            const filtered = byClient.clients.filter((c) =>
              !query.trim() ||
              `${c.patient_first_name} ${c.patient_last_name}`.toLowerCase().includes(query.toLowerCase())
            );
            if (filtered.length === 0) {
              return <div className="py-10 text-center text-sm text-stone-500">{query ? "No matches." : "No claims yet. Upload a HHA claim search CSV to get started."}</div>;
            }
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr className="text-[10px] uppercase tracking-widest text-stone-500">
                      <th className="text-left font-semibold px-3 py-2.5">Client</th>
                      <th className="text-left font-semibold px-3 py-2.5">DOB</th>
                      <th className="text-left font-semibold px-3 py-2.5">Service Period</th>
                      <th className="text-right font-semibold px-3 py-2.5">Lines</th>
                      <th className="text-right font-semibold px-3 py-2.5">Total Hrs</th>
                      <th className="text-right font-semibold px-3 py-2.5">Billed</th>
                      <th className="text-right font-semibold px-3 py-2.5">Paid</th>
                      <th className="text-right font-semibold px-3 py-2.5">Outstanding</th>
                      <th className="text-left font-semibold px-3 py-2.5">Payer(s)</th>
                    </tr>
                  </thead>
                  <tbody data-testid="sa-billing-by-client-body">
                    {filtered.map((c, idx) => {
                      const name = `${c.patient_last_name || ""}, ${c.patient_first_name || ""}`.replace(/^,\s*|,\s*$/, "");
                      const period = c.first_service && c.last_service ? (
                        c.first_service === c.last_service ? c.first_service : `${c.first_service} → ${c.last_service}`
                      ) : "—";
                      return (
                        <tr key={`${name}-${idx}`} data-testid={`sa-billing-client-row-${idx}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                          <td className="px-3 py-2.5 font-medium text-stone-900">{name || "—"}</td>
                          <td className="px-3 py-2.5 text-stone-700 font-mono text-xs">{c.patient_dob || "—"}</td>
                          <td className="px-3 py-2.5 text-stone-700 font-mono text-xs">{period}</td>
                          <td className="px-3 py-2.5 text-stone-700 text-right">{c.line_count}</td>
                          <td className="px-3 py-2.5 text-stone-700 text-right">{c.total_hours ? c.total_hours.toFixed(1) : "—"}</td>
                          <td className="px-3 py-2.5 text-stone-900 text-right font-medium">{money(c.total_billed)}</td>
                          <td className="px-3 py-2.5 text-emerald-700 text-right font-medium">{money(c.total_paid)}</td>
                          <td className={`px-3 py-2.5 text-right font-semibold ${c.total_outstanding > 0.01 ? "text-red-700" : "text-emerald-700"}`}>{money(c.total_outstanding)}</td>
                          <td className="px-3 py-2.5 text-stone-700 text-xs">{(c.payers || []).slice(0, 2).join(", ")}{c.payers && c.payers.length > 2 ? ` +${c.payers.length - 2}` : ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-stone-50 border-t-2 border-stone-300">
                    <tr className="text-stone-900 font-semibold">
                      <td colSpan="3" className="px-3 py-3">{byClient.totals.client_count} clients · {byClient.totals.line_count} lines</td>
                      <td colSpan="2"></td>
                      <td className="px-3 py-3 text-right">{money(byClient.totals.total_billed)}</td>
                      <td className="px-3 py-3 text-right text-emerald-700">{money(byClient.totals.total_paid)}</td>
                      <td className={`px-3 py-3 text-right ${byClient.totals.total_outstanding > 0.01 ? "text-red-700" : "text-emerald-700"}`}>{money(byClient.totals.total_outstanding)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()
        ) : data.claims.length === 0 ? (
          <div className="py-10 text-center text-sm text-stone-500">
            {query ? "No matches." : "No claims yet. Upload a HHA claim search CSV to get started."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-[10px] uppercase tracking-widest text-stone-500">
                  <th className="text-left font-semibold px-3 py-2.5">Client</th>
                  <th className="text-left font-semibold px-3 py-2.5">DOB</th>
                  <th className="text-left font-semibold px-3 py-2.5">Payer</th>
                  <th className="text-left font-semibold px-3 py-2.5">Service dates</th>
                  <th className="text-left font-semibold px-3 py-2.5">Proc</th>
                  <th className="text-right font-semibold px-3 py-2.5">Hrs</th>
                  <th className="text-right font-semibold px-3 py-2.5">Billed</th>
                  <th className="text-right font-semibold px-3 py-2.5">Paid</th>
                  <th className="text-right font-semibold px-3 py-2.5">Balance</th>
                  <th className="text-left font-semibold px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody data-testid="sa-billing-table-body">
                {data.claims.map((c) => {
                  const balance = (c.total_billed || 0) - (c.total_paid || 0);
                  const name = `${c.patient_last_name || ""}, ${c.patient_first_name || ""}`.replace(/^,\s*|,\s*$/, "");
                  const svc = c.service_end && c.service_end !== c.service_begin ? `${c.service_begin} → ${c.service_end}` : c.service_begin;
                  return (
                    <tr key={c.id} data-testid={`sa-billing-row-${c.id}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                      <td className="px-3 py-2.5 font-medium text-stone-900">{name || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-700 font-mono text-xs">{c.patient_dob || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-700">{c.payer || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-700 font-mono text-xs">{svc || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-700 font-mono text-xs">{c.procedure_code || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-700 text-right">{c.total_hours || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-900 text-right font-medium">{money(c.total_billed)}</td>
                      <td className="px-3 py-2.5 text-emerald-700 text-right font-medium">{money(c.total_paid)}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold ${balance > 0.01 ? "text-red-700" : "text-emerald-700"}`}>{money(balance)}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center rounded-full bg-stone-100 border border-stone-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-700">{c.claim_status || "—"}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => deleteOne(c.id)} className="text-stone-400 hover:text-red-600 transition-colors" title="Remove this claim line">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, tone = "stone" }) {
  const cls = {
    stone: "border-stone-200 bg-stone-50/50 text-stone-900",
    emerald: "border-emerald-200 bg-emerald-50/50 text-emerald-700",
    red: "border-red-200 bg-red-50/50 text-red-700",
  }[tone] || "";
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="mt-1 font-display text-lg font-medium">{value}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Insurance Quote Sheets (RPS) — SuperAdmin-only side workflow.
// Fill-out-online + pixel-perfect print on the carrier's original PDF.
// ---------------------------------------------------------------------------
function InsuranceQuotesPanel() {
  const [forms, setForms] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {form_id, data, id?} or null
  const [activityFor, setActivityFor] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/superadmin/insurance-quotes/forms").then((r) => r.data?.forms || []),
      api.get("/superadmin/insurance-quotes").then((r) => r.data?.quotes || []),
    ])
      .then(([f, q]) => { setForms(f); setQuotes(q); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const formMap = useMemo(() => Object.fromEntries(forms.map((f) => [f.form_id, f])), [forms]);

  const startNew = (form_id) => setEditing({ form_id, data: {}, id: null });
  const openExisting = (q) => setEditing({ form_id: q.form_id, data: q.data || {}, id: q.id, insured_name: q.insured_name });

  const remove = async (q) => {
    if (!window.confirm(`Delete quote for ${q.insured_name || "this insured"}?`)) return;
    try { await api.delete(`/superadmin/insurance-quotes/${q.id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const printQuote = async (q) => {
    try {
      const res = await api.get(`/superadmin/insurance-quotes/${q.id}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyFillLink = async (q) => {
    try {
      const r = await api.get(`/superadmin/insurance-quotes/${q.id}/fill-link`);
      const url = r.data?.fill_url;
      if (!url) throw new Error("No link available");
      await navigator.clipboard.writeText(url);
      toast.success("Fill link copied. Paste it into a chat or new email.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="sa-insurance-quotes" className="rounded-2xl border border-stone-200 bg-white p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-700 text-white grid place-items-center">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-xl text-stone-900">Insurance quotes (RPS)</div>
            <div className="text-xs text-stone-600">Fill the RPS quote sheets online; print → original PDF with your data overlaid.</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {forms.map((f) => (
            <Button
              key={f.form_id}
              data-testid={`sa-quote-new-${f.form_id}`}
              onClick={() => startNew(f.form_id)}
              className="bg-sky-700 hover:bg-sky-800 text-white rounded-lg h-9 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> {f.name}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-sky-700" /></div>
      ) : quotes.length === 0 ? (
        <div className="text-sm text-stone-500 italic py-6 text-center border border-dashed border-stone-300 rounded-xl">
          No saved quotes yet. Click a form above to start.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid="sa-quote-list" className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-stone-500">
              <tr className="border-b border-stone-200">
                <th className="text-left py-2 px-3">Insured</th>
                <th className="text-left py-2 px-3">Form</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Updated</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} data-testid={`sa-quote-row-${q.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="py-2 px-3 font-medium text-stone-900">{q.insured_name || <span className="italic text-stone-400">— unnamed —</span>}</td>
                  <td className="py-2 px-3 text-stone-600">{formMap[q.form_id]?.name || q.form_id}</td>
                  <td className="py-2 px-3">
                    <div className="flex flex-col gap-1 items-start">
                      {q.status === "completed" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-widest">
                          <Check className="w-2.5 h-2.5" /> Submitted
                        </span>
                      ) : q.awaiting_insured ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] uppercase tracking-widest">
                          <Clock className="w-2.5 h-2.5" /> Awaiting insured
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-50 text-stone-600 border border-stone-200 text-[10px] uppercase tracking-widest">Draft</span>
                      )}
                      {(q.email_history || []).length > 0 && (
                        <button
                          data-testid={`sa-quote-activity-${q.id}`}
                          type="button"
                          onClick={() => setActivityFor(q)}
                          className="text-[10px] text-stone-600 hover:text-sky-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
                          title="Show full activity"
                        >
                          <Mail className="w-2.5 h-2.5" />
                          {q.email_history.length} email{q.email_history.length === 1 ? "" : "s"} sent
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-stone-500">{q.updated_at?.slice(0, 10)}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <Button data-testid={`sa-quote-edit-${q.id}`} variant="outline" size="sm" onClick={() => openExisting(q)} className="rounded-lg h-7 text-xs mr-1 border-stone-300">
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button data-testid={`sa-quote-print-${q.id}`} variant="outline" size="sm" onClick={() => printQuote(q)} className="rounded-lg h-7 text-xs mr-1 border-sky-300 text-sky-700 hover:bg-sky-50">
                      <Printer className="w-3 h-3 mr-1" /> Print
                    </Button>
                    {(q.awaiting_insured || q.status === "completed") && (
                      <Button data-testid={`sa-quote-copylink-${q.id}`} variant="outline" size="sm" onClick={() => copyFillLink(q)} className="rounded-lg h-7 text-xs mr-1 border-amber-300 text-amber-700 hover:bg-amber-50">
                        <LinkIcon className="w-3 h-3 mr-1" /> Copy link
                      </Button>
                    )}
                    <Button data-testid={`sa-quote-del-${q.id}`} variant="outline" size="sm" onClick={() => remove(q)} className="rounded-lg h-7 text-xs border-red-300 text-red-700 hover:bg-red-50">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <InsuranceQuoteEditor
          form={formMap[editing.form_id]}
          initialData={editing.data}
          quoteId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {activityFor && (
        <Dialog open onOpenChange={(o) => !o && setActivityFor(null)}>
          <DialogContent data-testid="sa-quote-activity-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-lg">Email activity</DialogTitle>
              <DialogDescription className="text-xs">
                {activityFor.insured_name || "—"} · {formMap[activityFor.form_id]?.name || activityFor.form_id}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(activityFor.email_history || []).length === 0 && (
                <div className="text-xs text-stone-500 italic">No emails sent yet.</div>
              )}
              {(activityFor.email_history || []).slice().reverse().map((ev, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 rounded-lg border border-stone-200 bg-stone-50/40">
                  <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${ev.kind === "fill_link" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {ev.kind === "fill_link" ? <LinkIcon className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                  </div>
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="font-medium text-stone-800">
                      {ev.kind === "fill_link" ? "Fill link sent to insured" : "Quote PDF emailed to RPS"}
                    </div>
                    <div className="text-stone-600 truncate">to {ev.to}{ev.cc && ev.cc.length ? `, cc ${ev.cc.join(", ")}` : ""}</div>
                    {ev.subject && <div className="text-stone-500 italic truncate">{ev.subject}</div>}
                    <div className="text-[10px] text-stone-400 mt-0.5">
                      {ev.sent_at ? new Date(ev.sent_at).toLocaleString() : ""}
                      {ev.sent_by ? ` · by ${ev.sent_by}` : ""}
                    </div>
                  </div>
                </div>
              ))}
              {activityFor.status === "completed" && activityFor.submitted_at && (
                <div className="flex items-start gap-2 p-2 rounded-lg border border-emerald-200 bg-emerald-50/40">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="font-medium text-emerald-800">Insured submitted the form</div>
                    <div className="text-[10px] text-emerald-700/70 mt-0.5">{new Date(activityFor.submitted_at).toLocaleString()}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-2 border-t border-stone-200 mt-3">
              <Button variant="outline" onClick={() => setActivityFor(null)} className="rounded-lg h-9 text-xs">Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function _groupBySection(fields) {
  const out = new Map();
  fields.forEach((f) => {
    const k = f.section || "General";
    if (!out.has(k)) out.set(k, []);
    out.get(k).push(f);
  });
  return Array.from(out.entries());
}

function InsuranceQuoteEditor({ form, initialData, quoteId, onClose, onSaved }) {
  const [data, setData] = useState(initialData || {});
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [emailKind, setEmailKind] = useState(null); // "rps" | "insured" | null
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiFile, setAiFile] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const aiFileRef = useRef(null);

  const update = (id, val) => setData((d) => ({ ...d, [id]: val }));
  const updateRow = (tableKey, idx, colId, val) => {
    setData((d) => {
      const rows = [...(d[tableKey] || [])];
      while (rows.length <= idx) rows.push({});
      rows[idx] = { ...rows[idx], [colId]: val };
      return { ...d, [tableKey]: rows };
    });
  };

  const runAiExtract = async () => {
    if (!aiText.trim() && !aiFile) {
      toast.error("Paste an email or attach a file first.");
      return;
    }
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("form_id", form.form_id);
      if (aiText.trim()) fd.append("text", aiText.trim());
      if (aiFile) fd.append("file", aiFile);
      const r = await api.post("/superadmin/insurance-quotes/ai-extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const extracted = r.data?.data || {};
      // Merge: AI values fill blanks first, but also overwrite existing fields
      // because the user explicitly asked for AI pre-fill.
      setData((d) => {
        const merged = { ...d };
        Object.entries(extracted).forEach(([k, v]) => {
          if (v === null || v === undefined || v === "") return;
          if (Array.isArray(v)) {
            merged[k] = v;
          } else {
            merged[k] = v;
          }
        });
        return merged;
      });
      const count = Object.keys(extracted).length;
      toast.success(`AI extracted ${count} field${count === 1 ? "" : "s"}.`);
      setAiOpen(false);
      setAiText("");
      setAiFile(null);
      if (aiFileRef.current) aiFileRef.current.value = "";
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      let id = quoteId;
      if (!id) {
        const r = await api.post("/superadmin/insurance-quotes", { form_id: form.form_id, data });
        id = r.data?.id;
        toast.success("Quote created.");
      } else {
        await api.put(`/superadmin/insurance-quotes/${id}`, { data });
        toast.success("Saved.");
      }
      onSaved && onSaved(id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const saveAndPrint = async () => {
    setPrinting(true);
    try {
      let id = quoteId;
      if (!id) {
        const r = await api.post("/superadmin/insurance-quotes", { form_id: form.form_id, data });
        id = r.data?.id;
      } else {
        await api.put(`/superadmin/insurance-quotes/${id}`, { data });
      }
      const res = await api.get(`/superadmin/insurance-quotes/${id}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Saved & opened PDF.");
      onSaved && onSaved(id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPrinting(false); }
  };

  const headerFields = form?.header_fields || [];
  const sections = _groupBySection(form?.fields || []);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sa-quote-editor" className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {quoteId ? "Edit quote" : "New quote"} · {form?.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {form?.subtitle} — fields here drop directly onto the original RPS PDF when you print.
          </DialogDescription>
        </DialogHeader>

        {/* AI pre-fill panel */}
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 mb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-700" />
              <div className="text-xs font-medium text-stone-800">Pre-fill from a forwarded email or photo of an existing policy</div>
            </div>
            <Button
              data-testid="sa-quote-ai-toggle"
              variant="outline"
              size="sm"
              onClick={() => setAiOpen((o) => !o)}
              className="rounded-lg h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              {aiOpen ? "Hide" : "Open AI pre-fill"}
            </Button>
          </div>
          {aiOpen && (
            <div className="mt-3 space-y-2">
              <textarea
                data-testid="sa-quote-ai-text"
                rows={4}
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                placeholder="Paste the forwarded email body here — vehicle list, driver names, prior carrier, etc."
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-stone-300 focus:border-amber-500 outline-none"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  ref={aiFileRef}
                  data-testid="sa-quote-ai-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.txt"
                  onChange={(e) => setAiFile(e.target.files?.[0] || null)}
                  className="text-xs"
                />
                <div className="text-[11px] text-stone-500">PDF / photo of a policy works too — Gemini reads both.</div>
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setAiText(""); setAiFile(null); if (aiFileRef.current) aiFileRef.current.value = ""; }}
                    disabled={aiBusy}
                    className="rounded-lg h-7 text-xs"
                  >Clear</Button>
                  <Button
                    data-testid="sa-quote-ai-extract"
                    size="sm"
                    onClick={runAiExtract}
                    disabled={aiBusy || (!aiText.trim() && !aiFile)}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg h-7 text-xs"
                  >
                    {aiBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    Extract & fill
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {headerFields.length > 0 && (
          <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-3 mb-3 grid sm:grid-cols-2 gap-2">
            {headerFields.map((f) => (
              <QField key={f.id} field={f} value={data[f.id]} onChange={(v) => update(f.id, v)} />
            ))}
          </div>
        )}

        {sections.map(([section, fields]) => (
          <div key={section} className="mb-4">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-2">{section}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {fields.map((f) => (
                <QField key={f.id} field={f} value={data[f.id]} onChange={(v) => update(f.id, v)}
                        extraValue={f.extra_id ? data[f.extra_id] : undefined}
                        onExtraChange={(v) => f.extra_id && update(f.extra_id, v)} />
              ))}
            </div>
          </div>
        ))}

        <QTable title="Vehicles" tableKey="vehicles" cfg={form?.vehicle_table} data={data} updateRow={updateRow} />
        <QTable title="Drivers" tableKey="drivers" cfg={form?.driver_table} data={data} updateRow={updateRow} />
        <QTable title="Commodities" tableKey="commodities" cfg={form?.commodities_table} data={data} updateRow={updateRow} />
        <QTable title="Revenue / mileage history" tableKey="history" cfg={form?.history_table} data={data} updateRow={updateRow} rowLabels={form?.history_table?.row_labels} />

        <div className="flex justify-end gap-2 pt-3 border-t border-stone-200 mt-4 flex-wrap">
          <Button variant="outline" onClick={onClose} className="rounded-lg h-9 text-xs">Cancel</Button>
          <Button data-testid="sa-quote-save" onClick={save} disabled={saving || printing} className="bg-stone-700 hover:bg-stone-800 text-white rounded-lg h-9 text-xs">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save
          </Button>
          <Button data-testid="sa-quote-save-print" onClick={saveAndPrint} disabled={saving || printing} className="bg-sky-700 hover:bg-sky-800 text-white rounded-lg h-9 text-xs">
            {printing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1" />}
            Save & print
          </Button>
          <Button
            data-testid="sa-quote-email-rps"
            disabled={saving || printing}
            onClick={() => setEmailKind("rps")}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-9 text-xs"
          >
            <Mail className="w-3.5 h-3.5 mr-1" /> Email to RPS
          </Button>
          <Button
            data-testid="sa-quote-send-link"
            disabled={saving || printing}
            onClick={() => setEmailKind("insured")}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg h-9 text-xs"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Send link to insured
          </Button>
        </div>

        {emailKind && (
          <EmailDialog
            kind={emailKind}
            quoteId={quoteId}
            insuredName={data.insured || data.insured_name || ""}
            formName={form?.name}
            onClose={() => setEmailKind(null)}
            onBeforeSend={async () => {
              // Persist current state before sending so the PDF / link match
              // the latest edits.
              if (!quoteId) {
                const r = await api.post("/superadmin/insurance-quotes", { form_id: form.form_id, data });
                return r.data?.id;
              }
              await api.put(`/superadmin/insurance-quotes/${quoteId}`, { data });
              return quoteId;
            }}
            onSent={() => { setEmailKind(null); onSaved && onSaved(quoteId); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EmailDialog({ kind, quoteId, insuredName, formName, onClose, onBeforeSend, onSent }) {
  const isRps = kind === "rps";
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(isRps ? `RPS Quote — ${insuredName || "New submission"}` : "");
  const [message, setMessage] = useState(isRps
    ? `Please find the attached RPS quick quote sheet for ${insuredName || "the insured"}.\n\nLet me know if anything needs more detail.\n\nThanks,`
    : "");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!to.trim() || !to.includes("@")) {
      toast.error("Recipient email is required.");
      return;
    }
    setBusy(true);
    try {
      const id = await onBeforeSend();
      if (isRps) {
        await api.post(`/superadmin/insurance-quotes/${id}/email`, { to, cc, subject, body: message });
        toast.success(`Sent to ${to}.`);
      } else {
        const r = await api.post(`/superadmin/insurance-quotes/${id}/send-link`, { to, insured_label: insuredName, message });
        const url = r.data?.fill_url;
        if (url) {
          // Also copy the URL to clipboard for the operator — Gmail can land
          // in spam, so giving them a second channel (chat/SMS) is essential.
          try { await navigator.clipboard.writeText(url); } catch (_) {}
          toast.success(`Fill link emailed to ${to}. Link is also copied to clipboard.`, { duration: 8000 });
        } else {
          toast.success(`Fill link emailed to ${to}.`);
        }
      }
      onSent();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sa-quote-email-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            {isRps ? "Email this quote to RPS" : "Send fill link to insured"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isRps
              ? `Attaches the generated PDF for ${formName || "this quote"} and sends it via your Gmail.  Reply-to is set to childcheckoh@gmail.com.`
              : "Generates a one-time link that lets the insured complete the form themselves.  When they submit, the finished PDF is emailed back to you and the quote is marked Completed."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium text-stone-700 mb-1">To</div>
            <input data-testid="sa-quote-email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
                   placeholder={isRps ? "rps-rep@rpsins.com" : "client@example.com"}
                   className="w-full h-9 px-3 text-sm rounded-lg border border-stone-300 focus:border-sky-600 outline-none" />
          </div>
          {isRps && (
            <div>
              <div className="text-xs font-medium text-stone-700 mb-1">CC (optional, comma-separated)</div>
              <input data-testid="sa-quote-email-cc" type="text" value={cc} onChange={(e) => setCc(e.target.value)}
                     className="w-full h-9 px-3 text-sm rounded-lg border border-stone-300 focus:border-sky-600 outline-none" />
            </div>
          )}
          {isRps && (
            <div>
              <div className="text-xs font-medium text-stone-700 mb-1">Subject</div>
              <input data-testid="sa-quote-email-subject" type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                     className="w-full h-9 px-3 text-sm rounded-lg border border-stone-300 focus:border-sky-600 outline-none" />
            </div>
          )}
          <div>
            <div className="text-xs font-medium text-stone-700 mb-1">{isRps ? "Message" : "Note to the insured (optional)"}</div>
            <textarea data-testid="sa-quote-email-body" rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-stone-300 focus:border-sky-600 outline-none resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-stone-200 mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-lg h-9 text-xs">Cancel</Button>
          <Button data-testid="sa-quote-email-send" onClick={send} disabled={busy} className={`rounded-lg h-9 text-xs text-white ${isRps ? "bg-emerald-700 hover:bg-emerald-800" : "bg-amber-600 hover:bg-amber-700"}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            {isRps ? "Send to RPS" : "Send fill link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QField({ field, value, onChange, extraValue, onExtraChange }) {
  const tid = `qf-${field.id}`;
  if (field.type === "yesno") {
    return (
      <div className="rounded-lg border border-stone-200 p-2">
        <div className="text-xs font-medium text-stone-700 mb-1">{field.label}</div>
        <div className="flex gap-2 items-center flex-wrap">
          {["", "yes", "no"].map((opt) => (
            <button
              key={opt || "blank"}
              data-testid={`${tid}-${opt || "blank"}`}
              type="button"
              onClick={() => onChange(opt)}
              className={`px-2 py-1 rounded-md text-xs border transition-colors ${
                (value || "") === opt
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-stone-600 border-stone-300 hover:bg-stone-50"
              }`}
            >
              {opt === "" ? "—" : opt.toUpperCase()}
            </button>
          ))}
          {field.extra_id && (
            <input
              data-testid={`${tid}-extra`}
              type="text"
              placeholder="If yes, which units?"
              value={extraValue || ""}
              onChange={(e) => onExtraChange(e.target.value)}
              className="flex-1 min-w-[140px] h-7 px-2 text-xs rounded border border-stone-300 focus:border-sky-600 outline-none"
            />
          )}
        </div>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label data-testid={tid} className="flex items-center gap-2 rounded-lg border border-stone-200 p-2 cursor-pointer hover:bg-stone-50">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-emerald-700" />
        <span className="text-xs font-medium text-stone-700">{field.label}</span>
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2 rounded-lg border border-stone-200 p-2">
        <div className="text-xs font-medium text-stone-700 mb-1">{field.label}</div>
        <textarea
          data-testid={tid}
          rows={2}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1 text-xs rounded border border-stone-300 focus:border-sky-600 outline-none resize-none"
        />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-stone-200 p-2">
      <div className="text-xs font-medium text-stone-700 mb-1">{field.label}</div>
      <input
        data-testid={tid}
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-7 px-2 text-xs rounded border border-stone-300 focus:border-sky-600 outline-none"
      />
    </div>
  );
}

function QTable({ title, tableKey, cfg, data, updateRow, rowLabels }) {
  if (!cfg) return null;
  const rows = data[tableKey] || [];
  const maxRows = cfg.rows;
  return (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-2">{title}</div>
      <div className="overflow-x-auto rounded-lg border border-stone-200">
        <table data-testid={`qt-${tableKey}`} className="w-full text-xs">
          <thead className="bg-stone-50">
            <tr>
              {rowLabels && <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-stone-500">Period</th>}
              {cfg.columns.map((col) => (
                <th key={col.id} className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-stone-500">{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxRows }).map((_, idx) => (
              <tr key={idx} className="border-t border-stone-100">
                {rowLabels && <td className="px-2 py-1 text-stone-600 text-xs">{rowLabels[idx] || ""}</td>}
                {cfg.columns.map((col) => (
                  <td key={col.id} className="px-1 py-1">
                    <input
                      data-testid={`qt-${tableKey}-${idx}-${col.id}`}
                      type="text"
                      value={(rows[idx] || {})[col.id] || ""}
                      onChange={(e) => updateRow(tableKey, idx, col.id, e.target.value)}
                      className="w-full h-7 px-2 text-xs rounded border border-stone-200 focus:border-sky-600 outline-none"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}




// ---------------------------------------------------------------------------
// Reminder Settings — single control room for every automated email the
// system sends.  Toggle a reminder off, or change the hour-of-day (in the
// chosen timezone) at which it fires.  The scheduler honours both within
// 15 minutes of saving.
// ---------------------------------------------------------------------------
function ReminderSettingsPanel() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/superadmin/reminder-settings")
      .then((r) => { setSettings(r.data?.settings || {}); setDirty(false); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const update = (kind, patch) => {
    setSettings((s) => ({ ...s, [kind]: { ...s[kind], ...patch } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(settings).forEach(([k, v]) => {
        payload[k] = { enabled: !!v.enabled, hour_local: Number(v.hour_local), tz: v.tz };
      });
      await api.put("/superadmin/reminder-settings", { settings: payload });
      toast.success("Reminder settings saved.");
      setDirty(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const [testingKind, setTestingKind] = useState(null);
  const sendTest = async (kind) => {
    setTestingKind(kind);
    try {
      const r = await api.post(`/superadmin/reminder-settings/${kind}/test`);
      toast.success(`Test email sent to ${r.data?.sent_to || "your inbox"}.`, { duration: 6000 });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTestingKind(null); }
  };

  const TIMEZONES = [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Anchorage", "Pacific/Honolulu", "UTC",
  ];

  const hourLabel = (h) => {
    const d = new Date(); d.setHours(h, 0, 0, 0);
    return d.toLocaleTimeString([], { hour: "numeric", hour12: true });
  };

  return (
    <div data-testid="sa-reminder-settings" className="rounded-2xl border border-stone-200 bg-white p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-600 text-white grid place-items-center">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-xl text-stone-900">Reminder settings</div>
            <div className="text-xs text-stone-600">Single control room for every automated email. Toggle off or change the time of day each reminder fires.</div>
          </div>
        </div>
        <Button
          data-testid="sa-reminder-save"
          disabled={!dirty || saving}
          onClick={save}
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg h-9 text-xs disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
          Save changes
        </Button>
      </div>

      {loading ? (
        <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-amber-600" /></div>
      ) : (
        <div className="space-y-2">
          {Object.entries(settings).map(([kind, cfg]) => (
            <div
              key={kind}
              data-testid={`sa-reminder-${kind}`}
              className={`flex items-center gap-3 flex-wrap p-3 rounded-xl border ${cfg.enabled ? "border-emerald-200 bg-emerald-50/30" : "border-stone-200 bg-stone-50/40 opacity-70"}`}
            >
              <label className="flex items-center gap-2 cursor-pointer min-w-[260px] flex-1">
                <input
                  data-testid={`sa-reminder-${kind}-enabled`}
                  type="checkbox"
                  checked={!!cfg.enabled}
                  onChange={(e) => update(kind, { enabled: e.target.checked })}
                  className="w-4 h-4 accent-emerald-700"
                />
                <span className="text-sm font-medium text-stone-800">{cfg.label || kind}</span>
              </label>

              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-stone-500">Fire at</span>
                <select
                  data-testid={`sa-reminder-${kind}-hour`}
                  value={cfg.hour_local}
                  onChange={(e) => update(kind, { hour_local: Number(e.target.value) })}
                  disabled={!cfg.enabled}
                  className="h-8 px-2 rounded-lg border border-stone-300 bg-white text-stone-800 focus:border-amber-500 outline-none"
                >
                  {Array.from({ length: 24 }).map((_, h) => (
                    <option key={h} value={h}>{hourLabel(h)}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-stone-500">in</span>
                <select
                  data-testid={`sa-reminder-${kind}-tz`}
                  value={cfg.tz}
                  onChange={(e) => update(kind, { tz: e.target.value })}
                  disabled={!cfg.enabled}
                  className="h-8 px-2 rounded-lg border border-stone-300 bg-white text-stone-800 focus:border-amber-500 outline-none"
                >
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace("America/", "").replace("Pacific/", "").replace("_", " ")}</option>)}
                </select>
              </div>

              <Button
                data-testid={`sa-reminder-${kind}-test`}
                variant="outline"
                size="sm"
                disabled={testingKind !== null}
                onClick={() => sendTest(kind)}
                className="rounded-lg h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                title="Send a single sample email of this reminder to your inbox"
              >
                {testingKind === kind ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Send test
              </Button>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 text-[11px] text-stone-500 italic">
        Changes take effect within ~15 minutes (the scheduler polls on that cadence). Each reminder still fires at most once per day, even if you toggle it on/off multiple times.
      </p>
    </div>
  );
}
